import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { planApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAdminRights } from '../utils/roles';
import { PLAN_SETTINGS_PATH } from './AddToPlanCard';
import { XIcon } from './icons/LucideIcons';

const DISMISS_KEY = 'moveToCoreDismissedPeriod';

const readDismissed = () => { try { return localStorage.getItem(DISMISS_KEY); } catch (_) { return null; } };
const writeDismissed = (period) => { try { localStorage.setItem(DISMISS_KEY, period); } catch (_) { /* ignore */ } };

/**
 * Studio plan (S4): after two complete months over the plan's order cap,
 * admins see a prompt to move to Core at the top of every page. Orders are
 * never blocked. Closing it hides it for the rest of the month; it comes
 * back the next month if the shop is still over. Renders nothing for shops
 * without a capped plan (Core, Everything, Twigs) and for non-admin staff.
 */
const MoveToCoreBanner = () => {
  const { user } = useAuth();
  const isAdmin = hasAdminRights(user);
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    planApi.get()
      .then((r) => { if (!cancelled && r?.success) setStatus(r.data?.orders || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin || !status || !status.promptMoveTo || dismissed === status.period) return null;

  const over = status.previous.map((m) => m.orders);
  return (
    <div
      className="banner banner-green items-start rounded-none sm:items-center"
      role="status"
      data-testid="move-to-core"
    >
      <p className="flex-1">
        <span className="font-semibold">You've had more than {status.limit} orders two months running</span>
        {' '}({over.join(' and ')}). Studio is built for up to {status.limit} a month; nothing is blocked, but the Core plan has no order cap and adds dispatch, drivers, house accounts and unlimited staff.{' '}
        <Link to={PLAN_SETTINGS_PATH} className="font-semibold text-inherit underline underline-offset-2">Move to Core</Link>
      </p>
      <button
        type="button"
        onClick={() => { writeDismissed(status.period); setDismissed(status.period); }}
        className="shrink-0 rounded-full border border-floral-300 p-1 text-floral-900 transition-colors duration-[120ms] hover:bg-floral-200"
        aria-label="Hide for this month"
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};

export default MoveToCoreBanner;
